import { FC, useMemo, useRef } from "react"
import { debounce } from "lodash"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useTar } from "@/modules/i18n"
import { WysiwygEditor } from "@/components/WysiwygEditor"
import { workspaceChallengeIdAtom } from "@/store/workspaceAtoms"
import { apis } from "@modules/apiClients"
import { ChallengeService } from "@duck/api-definitions"
import { currentNotesAtom, isSavingWorkAtom, lastSaveWorkDateAtom } from "@/store/saveWorkAtoms"

export const NotesTab: FC = () => {
    const { t } = useTar()
    const challengeId = useAtomValue(workspaceChallengeIdAtom)
    const [notes, setNotes] = useAtom(currentNotesAtom)
    const notesRef = useRef(notes)
    notesRef.current = notes
    const setIsSaving = useSetAtom(isSavingWorkAtom)
    const setLastSaveDate = useSetAtom(lastSaveWorkDateAtom)

    const saveNotes = useMemo(
        () =>
            debounce(async (content: string) => {
                if (!challengeId) return
                setIsSaving(true)
                try {
                    await apis.challengeService.saveWork({
                        challengeId,
                        workState: {
                            // notes is a newer optional field; cast to extended WorkState
                            notes: content,
                        } as ChallengeService.WorkState & { notes?: string },
                    })
                    setLastSaveDate(new Date())
                } catch (err) {
                    console.error("Failed to save notes:", err)
                } finally {
                    setIsSaving(false)
                }
            }, 1000),
        [challengeId, setIsSaving, setLastSaveDate]
    )

    return (
        <div className="h-full min-h-full flex flex-col overflow-hidden">
            <WysiwygEditor
                content={notes}
                contentGetter={() => notesRef.current}
                onChange={(newNotes) => {
                    setNotes(newNotes)
                    saveNotes(newNotes)
                }}
                placeholder={t('common.notes')}
                className="max-w-none flex-1 min-h-0 p-0"
            />
        </div>
    )
}
